/**
 * ConversationList — left sidebar in the Support Center.
 * Extracted from Support.js during the iter-238 refactor (Feb 2026).
 */
import React from 'react';
import { MessageCircle, Plus, RefreshCw, Search, UserPlus, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ChannelIcon, StatusBadge, channelOf, formatDate } from './atoms';

export function ConversationList({
  isAr, t, language,
  loading, filteredConversations, selectedContact,
  setSelectedContact, setShowNewChat, fetchConversations,
  searchQuery, setSearchQuery,
  statusFilter, setStatusFilter,
}) {
  return (
    <div className="w-[380px] border-e border-white/10 flex flex-col">
      {/* Toolbar (moved from header): Refresh + New Chat */}
      <div className="p-4 border-b border-white/10 flex items-center gap-2">
        <Button
          onClick={() => setShowNewChat(true)}
          className="flex-1 bg-[#d4af37] hover:bg-[#e6c353] text-black font-bold shadow-[0_0_20px_rgba(212,175,55,0.25)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]"
          data-testid="new-chat-btn"
        >
          <Plus className="h-4 w-4 me-2" />
          {language === 'ar' ? 'محادثة جديدة' : 'New Chat'}
        </Button>
        <Button
          onClick={fetchConversations}
          variant="outline"
          size="icon"
          className="border-white/10 text-[#A1A1AA] hover:text-[#d4af37] hover:bg-white/5 hover:border-[#d4af37]/40 shrink-0"
          title={language === 'ar' ? 'تحديث' : 'Refresh'}
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="p-4 border-b border-white/10 space-y-3">
        <div className="relative">
          <Search className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A]`} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={language === 'ar' ? 'بحث...' : 'Search...'}
            className={`bg-white/5 border-white/10 text-white placeholder:text-[#71717A] ${language === 'ar' ? 'pr-10' : 'pl-10'} rounded-xl`}
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <Button
            size="sm"
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
            className={`rounded-full text-xs ${statusFilter === 'all' ? 'bg-[#d4af37] text-black font-bold' : 'border-white/10 text-[#A1A1AA]'}`}
          >
            {language === 'ar' ? 'الكل' : 'All'}
          </Button>
          <Button
            size="sm"
            variant={statusFilter === 'waiting' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('waiting')}
            className={`rounded-full text-xs ${statusFilter === 'waiting' ? 'bg-amber-500 text-white' : 'border-white/10 text-[#A1A1AA]'}`}
          >
            {language === 'ar' ? 'في الانتظار' : 'Waiting'}
          </Button>
          <Button
            size="sm"
            variant={statusFilter === 'in_progress' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('in_progress')}
            className={`rounded-full text-xs ${statusFilter === 'in_progress' ? 'bg-sky-500 text-white' : 'border-white/10 text-[#A1A1AA]'}`}
          >
            {language === 'ar' ? 'قيد المعالجة' : 'In Progress'}
          </Button>
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="h-20 bg-[#0a0a0a] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-8 text-center text-[#808080]">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{t('لا توجد محادثات', 'No conversations')}</p>
            <p className="text-sm mt-1">
              {t('اضغط "محادثة جديدة" للبدء', 'Click "New Chat" to start')}
            </p>
          </div>
        ) : (
          filteredConversations.map((contact) => (
            <button
              key={contact.id}
              onClick={() => setSelectedContact(contact)}
              className={`w-full p-4 flex items-start gap-3 hover:bg-[#2a2a4e] transition-colors text-right border-b border-[#2a2a4e]/50 ${
                selectedContact?.phone === contact.phone ? 'bg-[#2a2a4e]' : ''
              }`}
              data-testid={`contact-${contact.phone}`}
            >
              <div className="w-12 h-12 rounded-full bg-[#25D366]/20 flex items-center justify-center flex-shrink-0 relative">
                <User className="h-6 w-6 text-[#25D366]" />
                {/* Channel badge (bottom-right of avatar) */}
                <span className="absolute -bottom-0.5 -end-0.5 w-5 h-5 rounded-full bg-[#0a0a0a] border border-white/10 flex items-center justify-center shadow">
                  <ChannelIcon channel={channelOf(contact)} size={12} />
                </span>
                {contact.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {contact.unread_count}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-white truncate">{contact.name}</p>
                  <span className="text-[10px] text-[#808080]">{formatDate(contact.last_message_at, isAr, t)}</span>
                </div>
                <p className="text-sm text-[#808080] truncate mb-2">{contact.last_message}</p>
                <div className="flex items-center justify-between">
                  <StatusBadge status={contact.status || 'new'} isAr={isAr} />
                  {contact.assigned_name && (
                    <span className="text-[10px] text-[#606060] flex items-center gap-1">
                      <UserPlus className="h-3 w-3" />
                      {contact.assigned_name}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
